<template>
  <div
    class="relative bg-light-300 dark:bg-dark-800 cursor-pointer"
    :class="[
      size === 'md' ? 'rounded-3xl' : (
        size === 'sm' ? 'rounded-xl' : 'rounded-2xl'
      ),
      hasImage ? 'text-white' : 'text-light-950 dark:text-dark-50',
      (post.sentimentCount ?? 0 > 0) && isGreyed ? 'opacity-50' : ''
    ]"
    ref="$container"
  >
    <div
      class="flex items-center justify-center overflow-hidden relative mx-auto"
      :class="[
        size === 'sm' ? 'size-16' : 'aspect-[12/16]',
        hasImage ? 'bg-whale-950/10' : '',
        size === 'md' ? 'rounded-3xl' : (
          size === 'sm' ? 'rounded-xl' : 'rounded-2xl'
        )
      ]"
    >
      <img :src="post.medias[0]?.getSize('s')" class="absolute top-0 left-0 w-full h-full z-[2] object-cover object-center" draggable="false" v-if="hasImage" />

      <div class="space-y-1 min-h-16 flex items-center absolute top-0 left-0 w-[200%] text-dark-500 p-2" v-if="post.content && !hasImage && size === 'sm'">
        <div>
          <p class="text-4xs font-medium leading-1 line-clamp-1 break-all" v-if="post.attachments?.question">{{ post.attachments.question }}</p>
          <p class="font-bold text-2xs line-clamp-2 leading-3">{{ stripTags(post.content) }}</p>
        </div>
      </div>

      <div
        class="z-10 text-center space-y-3 absolute bottom-0 left-0 right-0 top-0 flex flex-col bg-dark-950/60 items-center justify-center"
        :class="[
          isSpicy ? (size === 'sm' ? 'backdrop-blur-[3px]' : 'backdrop-blur-md') : ''
        ]"
        v-if="post.isSpicy"
      >
        <ImageEmoji name="Fire" size="sm" class="z-10" :class="[ size === 'sm' ? 'size-8' : 'size-10' ]" />
      </div>
    </div>

    <div
      class="absolute flex items-center z-20"
      :class="[
        hasImage && size !== 'sm' ? 'bg-gradient-to-b from-charcoal-950/50 to-charcoal-950/0' : '',
        size === 'md' ? 'py-3 px-3' : (
          size === 'sm' ? '' : 'py-2 px-2.5'
        ),
        size === 'md' ? 'rounded-t-3xl' : (
          size === 'sm' ? 'rounded-t-xl' : 'rounded-t-2xl'
        ),
        size === 'sm' ? '-bottom-1 right-0' : 'top-0 left-0 right-0'
      ]"
    >
      <div class="flex items-center space-x-2 grow">
        <UserPicture :user="user" :class="[ size === 'sm' ? 'pointer-events-none' : '']" :size="size === 'md' ? 'sm' : (size === 'sm' ? '2xs' : '3xs')" is-hide-badges :is-link="size !== 'sm'" @click.stop />
        <p
          class="font-bold text-3xs grow"
          :class="[
            hasImage ? 'text-shadow' : '',
            size === 'md' ? 'text-xs' : 'text-3xs'
          ]"
          v-if="size !== 'sm'"
        >
          {{ dayjs(post.createdAt).fromNow(true) }}
        </p>
      </div>

      <div class="shrink-0 flex items-center space-x-1.5" @click.stop="() => isEditVisibility = true" v-if="isMine && size !== 'sm'">
        <div class="text-white bg-tiger-500 h-7 px-1.5 space-x-0.5 rounded-lg flex items-center justify-center" v-if="post.visibility === 'friends'">
          <IconBase name="cute/star-fill" :width="16" />
          <IconBase name="cute/angle-right-fill" class="rotate-90" :width="16" />
        </div>
        <div class="text-white bg-cobra-500 h-7 px-1.5 space-x-0.5 rounded-lg flex items-center justify-center" v-if="post.visibility === 'followers'">
          <IconBase name="cute/check-fill" :width="16" />
          <IconBase name="cute/angle-right-fill" class="rotate-90" :width="16" />

        </div>
        <div class="text-white bg-royal-800 h-7 px-1.5 space-x-0.5 rounded-lg flex items-center justify-center" v-if="post.visibility === 'public'">
          <IconBase name="cute/world-fill" :width="16" />
          <IconBase name="cute/angle-right-fill" class="rotate-90" :width="16" />
        </div>
      </div>
    </div>

    <div class="absolute top-0 left-0 text-dark-50 flex items-center justify-center right-0 bottom-0 z-[21] bg-dark-950/80 p-4" @click.stop v-if="isEditVisibility">
      <IconBase name="cute/spinner" class="spin" v-if="isLoading" />
      <div class="bg-dark-800 rounded-lg font-medium text-sm w-full" v-else>
        <div class="flex items-center py-2 px-3 space-x-1" @click.stop="() => onEditVisibility('public')">
          <IconBase name="cute/world-fill" :width="16" />
          <p>{{ $t('components.Post.visibility.public.titleShort') }}</p>
        </div>
        <div class="flex items-center py-2 px-3 border-t border-dark-600 space-x-1" @click.stop="() => onEditVisibility('followers')">
          <IconBase name="cute/check-fill" :width="16" />
          <p>{{ $t('components.Post.visibility.followers.titleShort') }}</p>
        </div>
        <div class="flex items-center py-2 px-3 border-t border-dark-600 space-x-1" @click.stop="() => onEditVisibility('friends')">
          <IconBase name="cute/star-fill" :width="16" />
          <p>{{ $t('components.Post.visibility.friends.titleShort') }}</p>
        </div>
      </div>
    </div>

    <div
      class="absolute bottom-0 left-0 font-medium w-full z-20"
      :class="[
        hasImage ? 'bg-gradient-to-t from-charcoal-950/90 to-charcoal-950/0' : '',
        size === 'md' ? 'rounded-b-3xl' : 'rounded-b-2xl'
      ]"
      v-if="size !== 'sm'"
    >
      <div
        class="max-w-xl mx-auto pt-4 px-3 pb-3"
        :class="[ size === 'md' ? ' space-y-2' : ' space-y-2' ]"
      >
        <p class="text-sm leading-tight font-bold line-clamp-2" v-if="post.attachments?.question && !isSpicy">{{ post.attachments.question }}</p>

        <div class="space-y-1" :class="[ hasImage ? 'text-shadow' : '' ]" v-if="post.content">
          <p :class="[
            size === 'md' ? 'text-sm leading-[1.15]' : 'text-xs leading-[1.15]', isSpicy ? 'line-clamp-1' : (hasImage ? 'line-clamp-2' : 'line-clamp-4')
          ]">{{ stripTags(post.content) }}</p>
        </div>

        <PostActions :post="post" class="grow" @like="() => sentimentsStore.skip({ postId: post._id })" :types="[ 'likes', 'comments' ]" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs'
import { Post } from '@/classes'
import UserStore from '@/store/user'
import UsersStore from '@/store/users'
import PostsStore from '@/store/posts'
import SentimentsStore from '@/store/sentiments'

const props = withDefaults(defineProps<{
  post: Post
  size?: 'sm' | 'base' | 'md'
  isSkip?: boolean
  isMine?: boolean
  isGreyed?: boolean
}>(), {
  size: 'base',
  isMine: false,
  isGreyed: true,
  isSkip: false
})

const { post } = toRefs(props)

const postsStore = PostsStore()
const sentimentsStore = SentimentsStore()
const usersStore = UsersStore()
const userStore = UserStore()
const { isSpicyUntil } = storeToRefs(userStore)

const isSpicy = computed(() => post.value.isSpicy && (isSpicyUntil.value ? isSpicyUntil.value <= new Date() : true))

const user = computed(() => usersStore.getItem(props.post.owner))
const hasImage = computed(() => post.value.medias?.[0]?.medias?.[0]?.src)

const isLoading = ref(false)
const isEditVisibility = ref(false)
const onEditVisibility = async (visibility: 'public' | 'followers' | 'friends') => {
  if (isLoading.value) return

  isLoading.value = true

  await postsStore.edit(props.post._id, {
    visibility
  })

  isLoading.value = false
  isEditVisibility.value = false
}

const $container = ref()

const { stop } = useIntersectionObserver($container,
  ([entry]) => {
    if (entry?.isIntersecting) {
      if (props.isSkip) {
        sentimentsStore.skip({
          postId: props.post._id
        })
      }

      stop()
    }
  },
  {
    threshold: 0.8
  }
)
</script>
